import { Box, useMediaQuery, useTheme } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { initialAlertState, useAlertStatus } from '../../../../customHook/useAlertStatus';
import { useAppSelector } from '../../../../store/hooks';
import zeplinColor from '../../../../theme/Color';
import {
  CalendarEvent,
  transformGoogleEvent,
  transformHuddleEvent,
  transformMicrosoftEvent,
  transformZoomEvent,
} from '../../../../utilities/eventUtilies';
import CustomAlert from '../../../shared/CustomAlert/CustomAlert';
import styles from './CalendarGrid.module.css';
import { CalendarHeader } from './components/CalendarHeader';
import { EventCreationPopOver } from './components/EventCreationPopOver';
import { EventPopOverContent } from './components/EventPopOverContent';
import { GridContentCell } from './components/GridContentCell';
import { TimeAxisColumn } from './components/TimeAxisColumn';

interface CalendarGridProps {
  startDate: Dayjs;
  endDate: Dayjs;
  timeSlotHeight?: number;
  startHour?: number;
  endHour?: number;
  selectedDate: dayjs.Dayjs | null;
  getTimezoneDisplayText: () => string;
}

export interface ProvisionalEvent {
  date: Dayjs;
  start: Dayjs;
  end: Dayjs;
  columnKey: string;
  duration: number;
}

const DEFAULT_DURATION_MINUTES = 60;
const timeColumnWidth = 150;
const dateColumnWidth = 184;

export const CalendarGrid: React.FC<CalendarGridProps> = ({
  startDate,
  endDate,
  startHour = 0,
  endHour = 23,
  selectedDate,
  getTimezoneDisplayText,
}: CalendarGridProps) => {
  const events = useAppSelector((state) => state.calendar.filteredEvents);
  const filters = useAppSelector((state) => state.calendar.filters);
  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.up('xs'));
  const rowHeight = isXS ? 70 : 115;
  console.log({ filters });
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent>();
  const [showGuests, setShowGuests] = useState(false);
  const [provisionalEvent, setProvisionalEvent] = useState<ProvisionalEvent | null>(null);
  const [createAnchorEl, setCreateAnchorEl] = useState<HTMLDivElement | null>(null);
  const [editEventData, setEditEventData] = useState<CalendarEvent | null>(null);
  const [isEditingExisting, setIsEditingExisting] = useState(false);

  const { alertState, startOperation, completeOperation, setAlertState, getAlertContent } =
    useAlertStatus();

  const processedEvents = useMemo(() => {
    return events.map((event) => {
      if (event.type === 'huddle') return transformHuddleEvent(event);
      if (event.type === 'Google') return transformGoogleEvent(event);
      if (event.type === 'MSTeams') return transformMicrosoftEvent(event);
      return transformZoomEvent(event);
    });
  }, [events]);

  const indexedEvents = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};

    processedEvents.forEach((event) => {
      const dateKey = event.start.format('YYYY-MM-DD');
      const hourKey = event.start.hour();
      const finalKey = `${dateKey}-${hourKey}`;

      if (!map[finalKey]) {
        map[finalKey] = [];
      }
      map[finalKey].push(event);
    });

    return map;
  }, [processedEvents]);

  const handleEditEvent = async () => {
    if (selectedEvent) {
      setIsEditingExisting(true);
      setEditEventData(selectedEvent);

      setProvisionalEvent({
        date: dayjs(selectedEvent.start),
        start: dayjs(selectedEvent.start),
        end: dayjs(selectedEvent.end),
        columnKey: dayjs(selectedEvent.start).format('YYYY-MM-DD'),
        duration: dayjs(selectedEvent.end).diff(dayjs(selectedEvent.start), 'minute'),
      });

      setCreateAnchorEl(anchorEl);
      setAnchorEl(null);
    }
  };

  const handleCloseCreate = () => {
    setProvisionalEvent(null);
    setCreateAnchorEl(null);
    setEditEventData(null);
    setIsEditingExisting(false);
  };

  const handleCellClick = (
    date: Dayjs,
    timeSlot: { hourIndex: number; hour24: string },
    targetElement: HTMLDivElement,
  ) => {
    const startDateTime = date.hour(timeSlot.hourIndex).minute(0);
    const endDateTime = startDateTime.add(DEFAULT_DURATION_MINUTES, 'minute');

    setProvisionalEvent({
      date: date,
      start: startDateTime,
      end: endDateTime,
      columnKey: date.format('YYYY-MM-DD'),
      duration: endDateTime.diff(startDateTime, 'minute'),
    });

    setCreateAnchorEl(targetElement);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const gridBodyRef = useRef<HTMLDivElement>(null);

  const handleShowGuests = () => {
    setShowGuests((prev) => !prev);
  };

  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = startHour; hour <= endHour; hour++) {
      slots.push({
        time: dayjs().hour(hour).minute(0).format('h:mm a'),
        hour24: String(hour).padStart(2, '0') + ':00',
        hourIndex: hour,
      });
    }
    return slots;
  }, [startHour, endHour]);

  const dateColumns = useMemo(() => {
    const columns = [];
    let currentDate = startDate;

    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
      columns.push({
        date: currentDate,
        dayName: currentDate.format('ddd'),
        dayNumber: currentDate.format('DD'),
        fullFormat: currentDate.format('ddd DD'),
        isToday: currentDate.isSame(dayjs(), 'day'),
        isSelected: selectedDate
          ? currentDate.isSame(selectedDate, 'day')
          : dayjs().isSame(currentDate, 'day'),
      });
      currentDate = currentDate.add(1, 'day');
    }
    return columns;
  }, [startDate, endDate, selectedDate]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCopyLink = (val: any) => {
    navigator.clipboard.writeText(val);
  };

  const hourEvents: { [key: string]: number } = {};

  useEffect(() => {
    if (gridBodyRef.current) {
      const nineAMIndex = timeSlots.findIndex((slot) => slot.hourIndex === 9);
      if (nineAMIndex !== -1) {
        const scrollY = nineAMIndex * rowHeight;
        gridBodyRef.current.scrollTop = scrollY;
      }
    }
  }, [rowHeight, timeSlots]);

  return (
    <Box
      className={styles['calendar-grid']}
      sx={{
        height: '100%',
        backgroundColor: zeplinColor.Neutral1,
      }}
    >
      <CalendarHeader dateColumns={dateColumns} getTimezoneDisplayText={getTimezoneDisplayText} />

      <Box
        ref={gridBodyRef}
        sx={{
          position: 'relative',
          flex: 1,
          minWidth: `${timeColumnWidth + dateColumnWidth * dateColumns.length}px`,
        }}
      >
        {timeSlots.map((timeSlot) => (
          <Box
            className={styles['time-slot-row']}
            key={timeSlot.hour24}
            sx={{
              display: 'flex',
              width: '100%',
              height: rowHeight,
            }}
          >
            <TimeAxisColumn timeSlot={timeSlot} />

            {dateColumns.map((dateCol, colIndex) => {
              const slotKey = `${dateCol.date.format('YYYY-MM-DD')}-${timeSlot.hourIndex}`;
              const slotEvents = indexedEvents[slotKey] || [];
              const isCurrentDate = dateCol.date.isSame(dayjs(), 'day');
              const isCurrentHour = timeSlot.hourIndex === dayjs().hour();
              return (
                <GridContentCell
                  colIndex={colIndex}
                  dateColumns={dateColumns}
                  dateCol={dateCol}
                  timeSlot={timeSlot}
                  slotEvents={slotEvents}
                  isCurrentDate={isCurrentDate}
                  isCurrentHour={isCurrentHour}
                  hourEvents={hourEvents}
                  handleClick={handleClick}
                  setSelectedEvent={setSelectedEvent}
                  handleCellClick={handleCellClick}
                  provisionalEvent={provisionalEvent}
                  isEditingExisting={isEditingExisting}
                />
              );
            })}
          </Box>
        ))}
      </Box>
      {selectedEvent && (
        <EventPopOverContent
          setAnchorEl={setAnchorEl}
          anchorEl={anchorEl}
          id={id}
          selectedEvent={selectedEvent}
          handleCopyLink={handleCopyLink}
          handleShowGuests={handleShowGuests}
          showGuests={showGuests}
          onEdit={handleEditEvent}
          startOperation={startOperation}
          completeOperation={completeOperation}
        />
      )}
      <EventCreationPopOver
        provisionalEvent={provisionalEvent}
        anchorEl={createAnchorEl}
        onClose={handleCloseCreate}
        setProvisionalEvent={setProvisionalEvent}
        data={editEventData}
        isEditingExisting={isEditingExisting}
        setIsEditingExisting={setIsEditingExisting}
        startOperation={startOperation}
        completeOperation={completeOperation}
      />
      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </Box>
  );
};
